/*
  The filter editor, ported from the app's filter modal (orgchart.html:3401-3519).

  Unlike everything else in this directory, nothing here is a command. A filter is a way of READING
  the project, not a change to it: it emits no event, advances no sequence number, reaches nobody
  else's screen, and appears in no history. That is why this is draft-with-Apply — the whole draft
  commits at once, there is nothing to invert, and there is no audit trail to keep tidy.

  The semantics are the app's, deliberately, because `passesFilters` is held to them by the parity
  harness and this editor must not be able to express something the engine reads differently:

    - AND across conditions, OR within one condition's values, inclusive min/max for numeric ranges.
    - No operators. There is no "contains" or "not equal to" — a categorical filter is a set of ticks.
    - One condition per field: the Add list excludes fields already in the draft (`:3468`).
    - A blank numeric value never satisfies a range, and a blank categorical value is selectable as
      "(No <label>)" rather than being unmatchable.

  Two things this adds. An empty condition is DROPPED on Apply (`:3409`), so a field added and never
  ticked cannot silently match nothing. And a condition whose field is not in the catalogue — from a
  preferences row written before a column was unmapped, or an import without `raw` — renders as a
  visibly broken chip with one click to remove it, rather than as a condition that quietly matches
  nobody and empties the chart.
*/
import { useMemo, useState } from 'react';
import type { FilterCondition } from '@integrate/contracts';
import {
  activeFilterCount,
  availableFields,
  getDistinctValues,
  type AvailableField,
  type SeparationState,
} from '@integrate/core';
import { Modal } from '../ui/Modal.js';

/** The app's cap on how many values one condition lists at a time (`orgchart.html:3403`). */
const VALUE_CAP = 200;

type Draft = FilterCondition & { search?: string };

function isBlank(f: FilterCondition): boolean {
  if (f.numeric && f.range) {
    const hasMin = f.range.min != null && f.range.min !== '';
    const hasMax = f.range.max != null && f.range.max !== '';
    return !hasMin && !hasMax;
  }
  return !f.values || f.values.length === 0;
}

export function FiltersEditor({
  state,
  filters,
  perspective,
  onApply,
  onClose,
}: {
  state: SeparationState;
  filters: readonly FilterCondition[];
  /** Facet values resolve through the perspective, so the picker shows the labels this view uses. */
  perspective: string;
  onApply(filters: FilterCondition[]): void;
  onClose(): void;
}): JSX.Element {
  const fields = useMemo(() => availableFields(state), [state]);
  const byId = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields]);

  const [draft, setDraft] = useState<Draft[]>(() => filters.map((f) => ({ ...f })));
  const [adding, setAdding] = useState('');

  const used = new Set(draft.map((f) => f.fieldId));
  const addable = fields.filter((f) => !used.has(f.id));

  const patch = (index: number, next: Partial<Draft>): void =>
    setDraft((prev) => prev.map((f, i) => (i === index ? { ...f, ...next } : f)));

  const add = (): void => {
    const field = byId.get(adding);
    if (!field) return;
    setDraft((prev) => [
      ...prev,
      field.numeric
        ? { fieldId: field.id, label: field.label, numeric: true, range: { min: null, max: null } }
        : { fieldId: field.id, label: field.label, values: [] },
    ]);
    setAdding('');
  };

  const apply = (): void => {
    // An added-but-untouched condition would match nothing on the app's own reading, so it is dropped
    // rather than applied — the original does the same at `:3409`.
    onApply(draft.filter((f) => !isBlank(f)).map(({ search: _search, ...f }) => f));
    onClose();
  };

  return (
    <Modal
      testId="filters-modal"
      title="Filters"
      onClose={onClose}
      wide
      footer={
        <>
          <span className="muted" data-testid="filters-active-count">
            {activeFilterCount(draft)} active
          </span>
          <span className="spacer" />
          <button data-testid="filters-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="primary" data-testid="filters-apply" onClick={apply}>
            Apply
          </button>
        </>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        Narrow the chart, the transfers picker and the RemainCo carrier list together. Conditions
        combine with <strong>and</strong>; values within one condition combine with{' '}
        <strong>or</strong>. This is <strong>yours alone</strong> — nobody else&rsquo;s view changes,
        and nothing is recorded in the project&rsquo;s history.
      </p>

      {draft.length === 0 && (
        <p className="muted" data-testid="filters-empty">
          No filters. Everyone is shown.
        </p>
      )}

      {draft.map((f, i) => {
        const field = byId.get(f.fieldId);
        return field ? (
          <Condition
            key={f.fieldId}
            state={state}
            condition={f}
            field={field}
            perspective={perspective}
            onPatch={(next) => patch(i, next)}
            onRemove={() => setDraft((prev) => prev.filter((_, j) => j !== i))}
          />
        ) : (
          <Broken
            key={f.fieldId}
            condition={f}
            onRemove={() => setDraft((prev) => prev.filter((_, j) => j !== i))}
          />
        );
      })}

      <div className="filter-add">
        <select
          data-testid="filter-add-field"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
        >
          <option value="">Add a filter on…</option>
          {addable.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
        <button data-testid="filter-add" disabled={!adding} onClick={add}>
          Add
        </button>
      </div>
    </Modal>
  );
}

/**
 * A condition naming a field the catalogue no longer offers.
 *
 * A saved preference outlives the mapping it was written against: unmap a column, or re-import without
 * retaining the raw rows, and the condition resolves to `''` for everybody and empties the chart. An
 * empty chart with no explanation is the worst possible answer, so it says what it is.
 */
function Broken({
  condition,
  onRemove,
}: {
  condition: FilterCondition;
  onRemove(): void;
}): JSX.Element {
  return (
    <div className="filter-cond broken" data-testid={`filter-broken-${condition.fieldId}`}>
      <div className="filter-cond-head">
        <strong>{condition.label || condition.fieldId}</strong>
        <span className="spacer" />
        <button data-testid={`filter-remove-${condition.fieldId}`} onClick={onRemove}>
          Remove
        </button>
      </div>
      <p className="muted" style={{ margin: 0 }}>
        This field is no longer in the data — its column may have been unmapped, or the project
        re-imported without its source columns. Nothing is being filtered on it.
      </p>
    </div>
  );
}

function Condition({
  state,
  condition,
  field,
  perspective,
  onPatch,
  onRemove,
}: {
  state: SeparationState;
  condition: Draft;
  field: AvailableField;
  perspective: string;
  onPatch(next: Partial<Draft>): void;
  onRemove(): void;
}): JSX.Element {
  const id = condition.fieldId;

  /*
    Facets come from `getDistinctValues`, which is the right function HERE and the wrong one for the
    assumptions grid: it resolves scenario renames, walks build-ups and folds blanks into
    "(No <label>)" — all three of which are exactly what a filter wants, because a filter matches what
    the chart is showing.
  */
  const all = useMemo(
    () =>
      field.numeric
        ? []
        : getDistinctValues(state, id, field.label, condition.perspective || perspective),
    [state, id, field.label, field.numeric, condition.perspective, perspective],
  );

  const search = (condition.search ?? '').toLowerCase();
  const matched = search ? all.filter((v) => v.toLowerCase().includes(search)) : all;
  const shown = matched.slice(0, VALUE_CAP);
  const selected = new Set(condition.values ?? []);

  const toggle = (value: string, on: boolean): void => {
    const next = new Set(selected);
    if (on) next.add(value);
    else next.delete(value);
    onPatch({ values: [...next] });
  };

  return (
    <div className="filter-cond" data-testid={`filter-cond-${id}`}>
      <div className="filter-cond-head">
        <strong>{field.label}</strong>
        <span className="muted" data-testid={`filter-selected-${id}`}>
          {field.numeric ? 'range' : `${selected.size} selected`}
        </span>
        <span className="spacer" />
        <button data-testid={`filter-remove-${id}`} onClick={onRemove}>
          Remove
        </button>
      </div>

      {field.numeric ? (
        <div className="filter-range">
          <label>Min</label>
          <input
            type="number"
            data-testid={`filter-min-${id}`}
            value={condition.range?.min == null ? '' : String(condition.range.min)}
            onChange={(e) =>
              onPatch({
                range: { min: e.target.value === '' ? null : Number(e.target.value), max: condition.range?.max ?? null },
              })
            }
          />
          <label>Max</label>
          <input
            type="number"
            data-testid={`filter-max-${id}`}
            value={condition.range?.max == null ? '' : String(condition.range.max)}
            onChange={(e) =>
              onPatch({
                range: { min: condition.range?.min ?? null, max: e.target.value === '' ? null : Number(e.target.value) },
              })
            }
          />
          <span className="muted">Blank is unbounded. A person with no value never matches a range.</span>
        </div>
      ) : (
        <>
          <div className="filter-search">
            <input
              type="text"
              data-testid={`filter-search-${id}`}
              placeholder="Search values…"
              value={condition.search ?? ''}
              onChange={(e) => onPatch({ search: e.target.value })}
            />
            {/* Selects the SEARCHED, CAPPED slice — what is on screen, not what is behind it. */}
            <button
              data-testid={`filter-selall-${id}`}
              onClick={() => onPatch({ values: [...new Set([...selected, ...shown])] })}
            >
              Select shown
            </button>
            <button data-testid={`filter-clearvals-${id}`} onClick={() => onPatch({ values: [] })}>
              Clear
            </button>
          </div>
          <div className="filter-values">
            {shown.map((value) => (
              <label className="check" key={value}>
                <input
                  type="checkbox"
                  data-testid={`filter-value-${id}-${value}`}
                  checked={selected.has(value)}
                  onChange={(e) => toggle(value, e.target.checked)}
                />
                <span>{value}</span>
              </label>
            ))}
          </div>
          {matched.length > VALUE_CAP && (
            <p className="muted" style={{ margin: '4px 0 0' }} data-testid={`filter-cap-note-${id}`}>
              Showing {VALUE_CAP} of {matched.length} — use search to narrow.
            </p>
          )}
        </>
      )}
    </div>
  );
}
